// Data model for the new calendar backend (calendar.newsquawk.com).
//
// Unlike the legacy Economic Calendar (flat shape from squawkasaservice), the
// new backend returns a discriminated union keyed by `classification`. Only
// `data` events carry the economic-release figures + a nested `category`; the
// other classifications (earning/supply/speaker/expiry/holiday/ad_hoc) carry
// their own fields. This is a slim, hand-written projection of the backend's
// OpenAPI response schemas — only the fields the widget actually renders.

export const CalendarV2State = {
  VISIBLE: "visible",
  UNKNOWN: "unknown",
  INVISIBLE: "invisible",
} as const;

export type CalendarV2StateValue =
  (typeof CalendarV2State)[keyof typeof CalendarV2State];

export type CalendarV2Classification =
  | "data"
  | "speaker"
  | "supply"
  | "earning"
  | "expiry"
  | "holiday"
  | "ad_hoc";

/** Nested category — present on `data` events only. */
export interface CalendarV2Category {
  id?: number;
  identity?: string | null;
  name?: string | null;
  display_name?: string | null;
  location?: string | null; // ISO 3166-1 code, e.g. "US"
  period?: string | null;
  unit?: string | null; // e.g. "%", "B", "M"
  currency?: string | null; // ISO 4217, e.g. "USD"
  importance?: string | null; // "high" | "normal" | "low"
  state?: CalendarV2StateValue | null;
  inverted?: boolean | null; // higher figure is worse when true
}

/** Fields shared by every classification. */
interface CalendarV2Base {
  id?: number;
  uuid: string;
  classification: CalendarV2Classification;
  occurs_at: string; // ISO-8601 UTC — primary sort/time key
  occurs_hint?: string | null;
  summary: string;
  detail?: string | null;
  disabled?: boolean;
  location?: string | null; // top-level ISO region (non-data events)
}

/** `data` — economic release with figures + category. */
export interface CalendarV2DataEvent extends CalendarV2Base {
  classification: "data";
  category?: CalendarV2Category | null;
  category_id?: number | null;
  survey_date?: string | null;
  previous?: string | null;
  expected?: string | null;
  actual?: string | null;
  revised?: string | null;
  net?: string | null;
  low?: string | null;
  high?: string | null;
  multiplier?: string | null;
}

/** `earning` — equity earnings release. */
export interface CalendarV2EarningEvent extends CalendarV2Base {
  classification: "earning";
  ticker?: string;
  currency?: string | null;
  eps_previous?: number | null;
  eps_actual?: number | null;
  eps_forecast?: number | null;
  revenue_previous?: number | null;
  revenue_actual?: number | null;
  revenue_forecast?: number | null;
  market_cap?: number | null;
}

/** `supply` — bond auction. */
export interface CalendarV2SupplyEvent extends CalendarV2Base {
  classification: "supply";
  isin?: string | null;
  sold_quantity?: number | null;
  bc_previous?: number | null;
  bc_actual?: number | null;
  average_previous?: number | null;
  average_actual?: number | null;
  tail_previous?: number | null;
  tail_actual?: number | null;
}

/** `speaker` — central-bank / official appearance. */
export interface CalendarV2SpeakerEvent extends CalendarV2Base {
  classification: "speaker";
  has_transcript?: boolean;
  has_q_and_a?: boolean;
  has_slides?: boolean;
  link?: string | null;
}

/** `expiry` | `holiday` | `ad_hoc` — base-only events. */
export interface CalendarV2SimpleEvent extends CalendarV2Base {
  classification: "expiry" | "holiday" | "ad_hoc";
}

export type CalendarV2Event =
  | CalendarV2DataEvent
  | CalendarV2EarningEvent
  | CalendarV2SupplyEvent
  | CalendarV2SpeakerEvent
  | CalendarV2SimpleEvent;

export const isDataEvent = (
  event: CalendarV2Event
): event is CalendarV2DataEvent => event.classification === "data";

/**
 * Visibility rule (mirrors the Solid client's `fetch_events`): drop disabled
 * entries, and for `data` events require the category to be explicitly visible.
 * Non-data classifications have no category and are always shown when enabled.
 */
export const isCalendarV2EventVisible = (event: CalendarV2Event): boolean => {
  if (event.disabled) return false;
  if (isDataEvent(event)) {
    return event.category?.state === CalendarV2State.VISIBLE;
  }
  return true;
};

/** Resolve the region code for a row, preferring the data category location. */
export const getEventLocation = (
  event: CalendarV2Event
): string | undefined => {
  if (isDataEvent(event)) {
    return event.category?.location ?? event.location ?? undefined;
  }
  return event.location ?? undefined;
};

/** Resolve the importance for a row (data-category concept only). */
export const getEventImportance = (
  event: CalendarV2Event
): string | undefined => {
  if (isDataEvent(event)) return event.category?.importance ?? undefined;
  return undefined;
};

/** Human-readable event name. */
export const getEventTitle = (event: CalendarV2Event): string => {
  if (isDataEvent(event)) {
    return (
      event.summary ||
      event.category?.display_name ||
      event.category?.name ||
      "Untitled Event"
    );
  }
  return event.summary || "Untitled Event";
};
